import React, { useState } from 'react';
import { Box, IconButton, Menu, MenuItem } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  IDPCard,
  CardHeader,
  EmployeeInfo,
  EmployeeDetails,
  EmployeeName,
  EmployeeRole,
  GoalTitle,
  TypeBadge,
  StatusBadge,
  CardFooter,
  DateInfo,
  OverdueAlert,
  ProgressSection,
  ProgressBar,
  ProgressFill,
  ProgressText,
  RAGIndicator,
  ActionsCount,
  SectionTitle,
  ActionItem,
  CheckboxIcon,
  ActionText,
} from '../styles';
import { DynamicIcon } from '../../Permissions/Roles/utils/iconMap';
import { EmployeeAvatar } from '../../common/EmployeeAvatar';
import { TRACKING_MODES } from '../../../constants/trackingModes';
import { formatDateOnly } from '../../../utils/dateOnly';

const isOverdue = (dateString) => {
  if (!dateString) return false;
  const deadline = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return deadline < today;
};

const getStatusColors = (status) => {
  const color = status?.color || '#6b7280';
  return {
    bg: `${color}15`,
    color: color,
  };
};

const getMeasurementTypeColors = (type) => {
  const color = type?.color || '#6b7280';
  return {
    bg: `${color}15`,
    color: color,
    border: `${color}50`,
  };
};

const IDPCardItem = ({ goal, onClick, onView, onEdit, isOwnGoal }) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const employee = goal.employee || {};
  const measurementType = goal.measurementType;
  const currentStatus = goal.currentStatus;
  const statusColors = getStatusColors(currentStatus);
  const typeColors = getMeasurementTypeColors(measurementType);
  const overdueActions = goal.actions?.filter(a => !a.completedAt && isOverdue(a.deadlineAt))?.length || 0;
  const isGoalOverdue = isOverdue(goal.deadlineAt);

  const renderTrackingInfo = () => {
    if (!measurementType) return null;

    const actions = goal.actions || [];
    const totalActions = goal._count?.actions || actions.length || 0;
    const trackingMode = measurementType.trackingMode;

    // For Checklist type - show completed/total
    if (trackingMode === TRACKING_MODES.CHECKLIST) {
      // An action is completed if it has completedAt set OR currentProgress is 100
      const completedActions = actions.filter(a => a.completedAt || a.currentProgress === 100)?.length || 0;

      // Only show if there are actions
      if (totalActions === 0) return null;

      const typeColor = measurementType.color || '#6b7280';

      return (
        <ProgressSection>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <ActionsCount>
              <CheckCircleIcon sx={{ fontSize: 16, color: typeColor }} />
              {completedActions} of {totalActions} completed
            </ActionsCount>
          </Box>
          <ProgressBar>
            <ProgressFill
              style={{ width: `${totalActions > 0 ? (completedActions / totalActions) * 100 : 0}%` }}
              $color={typeColor}
            />
          </ProgressBar>
        </ProgressSection>
      );
    }

    // For Progress type - show percentage
    if (trackingMode === TRACKING_MODES.PROGRESS) {
      // Only show if there are actions
      if (totalActions === 0) return null;

      // Calculate average progress from actions
      const totalProgress = actions.reduce((sum, a) => sum + (a.currentProgress || 0), 0);
      const avgProgress = actions.length > 0 ? Math.round(totalProgress / actions.length) : 0;
      const typeColor = measurementType.color || '#6b7280';

      return (
        <ProgressSection>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <ProgressText style={{ margin: 0 }}>{avgProgress}% complete</ProgressText>
          </Box>
          <ProgressBar>
            <ProgressFill
              style={{ width: `${avgProgress}%` }}
              $color={typeColor}
            />
          </ProgressBar>
        </ProgressSection>
      );
    }

    // For RAG type - show RAG indicators for actions
    if (trackingMode === TRACKING_MODES.RAG) {
      // Only show if there are actions
      if (totalActions === 0) return null;

      // Group actions by RAG status, keeping the status object for color
      const ragGroups = actions.reduce((acc, a) => {
        const ragStatus = a.currentRagStatus;
        const ragId = ragStatus?.id || 'unknown';
        if (!acc[ragId]) {
          acc[ragId] = { status: ragStatus, count: 0 };
        }
        acc[ragId].count += 1;
        return acc;
      }, {});

      return (
        <ProgressSection>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {Object.values(ragGroups).map(({ status, count }) => (
              <Box key={status?.id || 'unknown'} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <RAGIndicator $color={status?.color || '#9ca3af'} />
                <span style={{ fontSize: 13, color: '#6b7280' }}>{count}</span>
              </Box>
            ))}
          </Box>
        </ProgressSection>
      );
    }

    return null;
  };

  return (
    <IDPCard onClick={onClick}>
      <CardHeader>
        <EmployeeInfo>
          <EmployeeAvatar employee={employee} size={40} />
          <EmployeeDetails>
            <EmployeeName>{employee.fullName || 'Unknown Individual'}</EmployeeName>
            {(employee.orgPositions?.label || employee.orgGrades?.label) && (
              <EmployeeRole>
                {[employee.orgPositions?.label, employee.orgGrades?.label].filter(Boolean).join(' • ')}
              </EmployeeRole>
            )}
          </EmployeeDetails>
        </EmployeeInfo>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setMenuAnchor(e.currentTarget);
          }}
        >
          <MoreVertIcon />
        </IconButton>
      </CardHeader>

      <GoalTitle>{goal.title}</GoalTitle>

      <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <TypeBadge
          $bgColor={typeColors.bg}
          $color={typeColors.color}
          $borderColor={typeColors.border}
        >
          {measurementType?.icon && (
            <DynamicIcon iconName={measurementType.icon} sx={{ fontSize: 14, mr: 0.5 }} />
          )}
          {measurementType?.name || 'No Type'}
        </TypeBadge>
        <StatusBadge
          $bgColor={statusColors.bg}
          $color={statusColors.color}
        >
          {currentStatus?.name || 'Not Started'}
        </StatusBadge>
      </Box>

      {overdueActions > 0 && (
        <OverdueAlert>
          <WarningAmberIcon sx={{ fontSize: 16 }} />
          {overdueActions} action{overdueActions > 1 ? 's' : ''} past due date
        </OverdueAlert>
      )}

      {renderTrackingInfo()}

      <CardFooter>
        <DateInfo $overdue={isGoalOverdue}>
          {isGoalOverdue ? (
            <WarningAmberIcon sx={{ fontSize: 16 }} />
          ) : (
            <CalendarTodayIcon sx={{ fontSize: 16 }} />
          )}
          Target: {formatDateOnly(goal.deadlineAt) || 'No date'}
        </DateInfo>
        <ActionsCount>
          {(() => {
            const actionCount = goal._count?.actions || goal.actions?.length || 0;
            return `${actionCount} ${actionCount === 1 ? 'action' : 'actions'}`;
          })()}
          {(() => {
            const resourceCount = goal._count?.resources || goal.resources?.length || 0;
            return resourceCount > 0 ? ` • ${resourceCount} ${resourceCount === 1 ? 'resource' : 'resources'}` : '';
          })()}
        </ActionsCount>
      </CardFooter>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={() => { setMenuAnchor(null); if (onView) onView(goal); }}>
          View
        </MenuItem>
        {!isOwnGoal && (
          <MenuItem onClick={() => { setMenuAnchor(null); if (onEdit) onEdit(goal); }}>
            Edit
          </MenuItem>
        )}
      </Menu>
    </IDPCard>
  );
};

export default IDPCardItem;
